import React from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import '@gsa/afp-component-library/dist/css/index.css';
import {
  NotFound,
  PageFocus,
  PublicRoute,
  PrivateRoute,
} from '@gsa/afp-shared-ui-utils';
import StandardItems from '../../pages/standard-items';
import EquipmentCode from '../../pages/equipment-code';
import CatalogCodes from '../../pages/catalog-codes';
import StandardItemDetail from '../../pages/standard-items-detail';
import EquipmentCodeDetail from '../../pages/equipment-codes-detail';
import TaskDetail from '../../pages/task-detail';
import {
  SUBJECTS,
  OPERATIONS,
  LOCALSTORAGE_KEYS,
} from '../../utilities/constants';
import FedVehicleStandards from '../../pages/fvs';
import CommentsAdmin from '../../pages/comment-admin';
import ViewComments from '../../pages/comment-admin/view-comments';
import validateProcess from '../../utilities/feature-toggle/validate-process';

import Solicitations from '../../pages/solicitation';
import ViewSolicitation from '../../pages/solicitation/view-solicitation';
import CreateSolicitation from '../../pages/solicitation/create-solicitation';
import RenewSolicitation from '../../pages/solicitation/renew-solicitation';
import EditUpiidDetails from '../../pages/solicitation/edit-solicitation/1-upiid-details';
import EditOpenPeriod from '../../pages/solicitation/edit-solicitation/2-open-period';
import EditReopenPeriod from '../../pages/solicitation/edit-solicitation/3-reopen-period';
import EditSINSelection from '../../pages/solicitation/edit-solicitation/4-select-sin';
import EditSINQuantity from '../../pages/solicitation/edit-solicitation/5-assign-quantity';
import EditReview from '../../pages/solicitation/edit-solicitation/6-review';

import Bids from '../../pages/bids';
import BidDashboard from '../../pages/bid-dashboard';
import BidLineReviewAndSubmit from '../../pages/bid-dashboard/bid-line-details/review-and-submit';
import BidLineDetails from '../../pages/bid-dashboard/bid-line-details';
import SelectStandardItemForBid from '../../pages/bids/add-standard-items/index';
import MakeModelApproval from '../../pages/make-model-approval';
import AccessControlledRoute from '../../utilities/access-controlled-route/access-controlled-route';
import BidHistory from '../../pages/bids/bid-history';
import BidReports from '../../pages/bids/bid-reports';
import BidEvaluation from '../../pages/bid-evaluation/bid-search';
import BidLineEvaluation from '../../pages/bid-evaluation/bid-line-evaluation';
import Contract from '../../pages/contract';
import ContractReports from '../../pages/contract/contract-reports';
import RouteError from '../../components/RouterComponent/RouteError';

function AppRoutes() {
  // const { feature } = useFeatureToggle();
  const location = useLocation();
  const navigate = useNavigate();

  // Clear local storage keys for Standard Items and Equipment Codes, Catalog Code pages.
  LOCALSTORAGE_KEYS.forEach((storagekey) => {
    window.localStorage.removeItem(storagekey.key);
  });

  return (
    <>
      <PageFocus contextLocation={location} />
      <Routes>
        <Route errorElement={<RouteError />}>
          {/* TaskDetail */}
          <Route
            path="/tasks/:id"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                title="Peer Review"
                validate={validateProcess}
                featureName="FEDERAL_VEHICLE_STANDARD"
              >
                <TaskDetail />
              </PrivateRoute>
            }
          />

          {/* Fed Vehicle Standards */}
          <Route
            path="/vehicle-standards/*"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                validate={validateProcess}
                featureName="FEDERAL_VEHICLE_STANDARD"
              >
                <FedVehicleStandards />
              </PrivateRoute>
            }
          />

          {/* CatalogCodes */}
          <Route
            path="/codes/*"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                title="Catalog codes"
                operation={OPERATIONS.View}
                subject={SUBJECTS.CatalogCodes}
                validate={validateProcess}
                featureName="CATALOG_CODE"
              >
                <CatalogCodes />
              </PrivateRoute>
            }
          />

          {/* StandardItems */}
          <Route
            path="/standard-items"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                featureName="STANDARD_ITEM"
                title="Standard items"
                operation={OPERATIONS.Manage}
                subject={SUBJECTS.StandardItem}
                validate={validateProcess}
              >
                <StandardItems />
              </PrivateRoute>
            }
          />

          {/* StandardItemDetail */}
          <Route
            path="/standard-item/:key"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                featureName="STANDARD_ITEM"
                title="Standard Item Detail"
                operation={OPERATIONS.Manage}
                subject={SUBJECTS.StandardItem}
                validate={validateProcess}
              >
                <StandardItemDetail />
              </PrivateRoute>
            }
          />

          {/* EquipmentCodes */}
          <Route
            path="/equip-codes"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                featureName="EQUIPMENT_CODE"
                title="Equipment codes"
                operation={OPERATIONS.Manage}
                subject={SUBJECTS.EquipmentCodes}
                validate={validateProcess}
              >
                <EquipmentCode />
              </PrivateRoute>
            }
          />

          <Route
            path="/equip-codes/:year/:program/:code"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                featureName="EQUIPMENT_CODE"
                title="Equipment Code Detail"
                operation={OPERATIONS.Manage}
                subject={SUBJECTS.EquipmentCodes}
                validate={validateProcess}
              >
                <EquipmentCodeDetail />
              </PrivateRoute>
            }
          />

          {/* CommentsAdmin */}
          <Route
            path="/comments-admin"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                title="Comments Admin"
                operation={OPERATIONS.Manage}
                subject={SUBJECTS.FVSComments}
                validate={validateProcess}
                featureName="FEDERAL_VEHICLE_STANDARD"
              >
                <CommentsAdmin />
              </PrivateRoute>
            }
          />

          {/* ViewComments */}
          <Route
            path="/comments"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.View}
                subject={SUBJECTS.FVSComments}
                title="Comments"
                validate={validateProcess}
                featureName="FEDERAL_VEHICLE_STANDARD"
              >
                <ViewComments />
              </PrivateRoute>
            }
          />

          {/* Solicitations */}
          <Route
            path="/solicitations"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.Manage}
                subject={SUBJECTS.Solicitation}
                title="Solicitations"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <Solicitations />
              </PrivateRoute>
            }
          />

          {/* Solicitation detail */}
          <Route
            path="/solicitations/:id"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.View}
                subject={SUBJECTS.Solicitation}
                title="View solicitation"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <ViewSolicitation />
              </PrivateRoute>
            }
          />

          {/* Create New solicitation */}
          <Route
            path="/solicitations/create"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.Create}
                subject={SUBJECTS.Solicitation}
                title="New solicitation"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <CreateSolicitation />
              </PrivateRoute>
            }
          />

          {/* Renew Solicitation */}
          <Route
            path="/solicitations/renew/:id"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.Create}
                subject={SUBJECTS.Solicitation}
                title="Solicitation Detail"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <RenewSolicitation />
              </PrivateRoute>
            }
          />

          {/* Edit Solicitation Upiid Details */}
          <Route
            path="/solicitations/edit/:id"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.Create}
                subject={SUBJECTS.Solicitation}
                title="Edit solicitation"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <EditUpiidDetails />
              </PrivateRoute>
            }
          />

          {/* Solicitation edit open-period form */}
          <Route
            path="/solicitations/edit/:id/open-period"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.Create}
                subject={SUBJECTS.Solicitation}
                title="Edit solicitation"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <EditOpenPeriod />
              </PrivateRoute>
            }
          />

          {/* Solicitation edit Reopen Period Routes */}
          <Route
            path="/solicitations/edit/:id/reopen-period"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.Create}
                subject={SUBJECTS.Solicitation}
                title="Edit solicitation"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <EditReopenPeriod />
              </PrivateRoute>
            }
          />

          {/* Solicitation edit SIN Selection Routes */}
          <Route
            path="/solicitations/edit/:id/select-sin"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.Create}
                subject={SUBJECTS.Solicitation}
                title="Select Standard Item"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <EditSINSelection />
              </PrivateRoute>
            }
          />

          {/* Solicitation edit Assigned Quantity Routes */}
          <Route
            path="/solicitations/edit/:id/assign-quantity"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.Create}
                subject={SUBJECTS.Solicitation}
                title="Assign quantity"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <EditSINQuantity />
              </PrivateRoute>
            }
          />

          {/* Solicitation edit Review Routes */}
          <Route
            path="/solicitations/edit/:id/review"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.Create}
                subject={SUBJECTS.Solicitation}
                title="Review"
                validate={validateProcess}
                featureName="SOLICITATION"
              >
                <EditReview />
              </PrivateRoute>
            }
          />

          {/* Bids */}
          <Route
            path="/bids"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.View}
                subject={SUBJECTS.PSOQuestionnaire}
                title="Bids"
                validate={validateProcess}
                featureName="BIDS"
              >
                <Bids />
              </PrivateRoute>
            }
          />

          <Route
            path="/bids/bid-center/bid-history"
            element={
              <AccessControlledRoute
                contextLocation={location}
                contextNavigation={navigate}
                component={BidHistory}
                operation={OPERATIONS.View}
                subject={SUBJECTS.PSOQuestionnaire}
                title="Bid history"
                validate={validateProcess}
                featureName="BIDS"
              />
            }
          />

          <Route
            path="/bids/:bidId/:solId/add-standard-items"
            element={
              <AccessControlledRoute
                contextLocation={location}
                contextNavigation={navigate}
                component={SelectStandardItemForBid}
                operation={OPERATIONS.View}
                subject={SUBJECTS.PSOQuestionnaire}
                title="Add Standard Items"
                validate={validateProcess}
                featureName="BIDS"
              />
            }
          />

          <Route
            path="/solicitations/:solId/bid-dashboard/:bidId"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                title="Bid Dashboard"
                validate={validateProcess}
                featureName="BIDS"
              >
                <BidDashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/solicitations/:solId/bid-dashboard/:bidId/bid-line-details/:bidLineId/review-and-submit"
            element={
              <AccessControlledRoute
                contextLocation={location}
                contextNavigation={navigate}
                component={BidLineReviewAndSubmit}
                title="Bid line review and submit page"
                validate={validateProcess}
                featureName="BIDS"
              />
            }
          />

          <Route
            path="/solicitations/:solId/bid-dashboard/:bidId/bid-line-details/:bidLineId"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                title="Bid line details"
                validate={validateProcess}
                featureName="BIDS"
              >
                <BidLineDetails />
              </PrivateRoute>
            }
          />

          {/* Proposal evaluation routes*/}

          <Route
            path="/proposal-evaluation"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation="review"
                subject="Bid"
                title="Proposal evaluation"
              >
                <BidEvaluation />
              </PrivateRoute>
            }
          />

          <Route
            path="/proposal-evaluation/bid/:bidId"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation="review"
                subject="Bid"
                title="Line Items Evaluation"
              >
                <BidLineEvaluation />
              </PrivateRoute>
            }
          />

          {/* Contract Routes */}

          <Route
            path="/contract/*"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation={OPERATIONS.View}
                subject={SUBJECTS.Contract}
                title="Contracts"
                // feature={feature(Contract, 'contracts')}
              >
                <Contract />
              </PrivateRoute>
            }
          />

          {/* make-model routes */}

          <Route
            path="/makes-and-models"
            element={
              <PrivateRoute
                contextLocation={location}
                contextNavigation={navigate}
                operation="view"
                subject="MakeModel"
                title="Makes and models"
              >
                <MakeModelApproval />
              </PrivateRoute>
            }
          />

          {/* Report routes  */}

          <Route
            path="/bid-reports"
            element={
              <PrivateRoute
                operation="view"
                subject="BidReport"
                title="Bid reports"
                contextLocation={location}
                contextNavigation={navigate}
              >
                <BidReports key="bid-reports" />
              </PrivateRoute>
            }
          />

          <Route
            path="/fvs-reports"
            element={
              <PrivateRoute
                title="Federal Vehicle Standards reports"
                contextLocation={location}
                contextNavigation={navigate}
              >
                <BidReports key="fvs-reports" />
              </PrivateRoute>
            }
          />

          <Route
            path="/contract-reports"
            element={
              <PrivateRoute
                operation="view"
                subject="ContractReport"
                title="Contract reports"
                contextLocation={location}
                contextNavigation={navigate}
              >
                <ContractReports />
              </PrivateRoute>
            }
          />

          {/* Catch all route */}
          <Route
            path="*"
            element={
              <PublicRoute title="Sorry for the Detour">
                <NotFound />
              </PublicRoute>
            }
          />
        </Route>
      </Routes>
    </>
  );
}

export default AppRoutes;
